import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import { useWidgetConsent } from '../consent/useWidgetConsent';
import { getTenantGoogleAnalyticsMeasurementId } from '../config/tenantConfig';
import { AnalyticsContext } from './AnalyticsContext';
import { buildAnalyticsEnvelope } from './buildAnalyticsEnvelope';
import {
  clearGoogleAnalyticsConsent,
  initializeGoogleAnalyticsMeasurement,
  sendGoogleAnalyticsEvent,
  syncGoogleAnalyticsConsent,
} from './googleAnalytics/googleAnalytics';
import {
  WIDGET_ANALYTICS_DOM_EVENT_NAME,
  type WidgetAnalyticsEvent,
  type WidgetAnalyticsEventName,
  type WidgetAnalyticsRuntimeMetadata,
} from './types';

interface AnalyticsProviderProps extends WidgetAnalyticsRuntimeMetadata {
  hostElement?: HTMLElement | null;
  children: ComponentChildren;
}

/**
 * The main AnalyticsProvider component that manages the analytics context and dispatches events from the host element
 *
 * Events should be picked up by integrators via a DOM event listener on the host element or any ancestor in the DOM,
 * and forwarded to the integrator's analytics vendor(s) of choice if required.
 *
 * For tenants with a Google Analytics measurement ID, the provider bootstraps a
 * measurement-scoped gtag/dataLayer pair and keeps Google Consent Mode in sync with
 * the widget consent state.
 */
export function AnalyticsProvider({
  hostElement,
  tenant,
  initialConfig,
  buildInfo,
  children,
}: AnalyticsProviderProps) {
  const { consentState } = useWidgetConsent();
  const googleAnalyticsInstanceId = useRef(Symbol('googleAnalytics'));
  const measurementId = useMemo(
    () => getTenantGoogleAnalyticsMeasurementId(tenant),
    [tenant],
  );

  useEffect(() => {
    if (!measurementId) {
      return;
    }

    initializeGoogleAnalyticsMeasurement(measurementId);

    return () => {
      clearGoogleAnalyticsConsent(
        measurementId,
        googleAnalyticsInstanceId.current,
      );
    };
  }, [measurementId]);

  useEffect(() => {
    if (!measurementId) {
      return;
    }

    syncGoogleAnalyticsConsent(
      measurementId,
      googleAnalyticsInstanceId.current,
      consentState,
    );
  }, [measurementId, consentState]);

  // memoize the context value to avoid unnecessary re-renders of consumers
  const value = useMemo(
    () => ({
      metadata: {
        tenant,
        initialConfig,
        buildInfo,
      },
      /**
       * Track an analytics event by building an envelope and dispatching it from the host element as a CustomEvent
       */
      track: <TName extends WidgetAnalyticsEventName>(
        event: WidgetAnalyticsEvent<TName>,
      ) => {
        if (!hostElement) {
          return;
        }

        const envelope = buildAnalyticsEnvelope({
          tenant,
          buildInfo,
          event,
        });

        // dispatch the event from the host element, so integrators can listen for it in the DOM
        hostElement.dispatchEvent(
          new CustomEvent(WIDGET_ANALYTICS_DOM_EVENT_NAME, {
            detail: envelope,
            bubbles: true,
            composed: true,
          }),
        );

        // For our own internal analytics, we send the event directly to Google Analytics
        if (measurementId) {
          initializeGoogleAnalyticsMeasurement(measurementId);
          sendGoogleAnalyticsEvent(measurementId, event.name, {
            activity: event.activity,
            page: event.page,
            ...event.payload,
          });
        }
      },
    }),
    [hostElement, tenant, initialConfig, buildInfo, measurementId],
  );

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
}
